// Admin "Payment requests": charge someone an amount after checkout has
// closed. The payer gets an emailed link, logs in and pays by card; the
// organizer's share goes to the event's Stripe account and the Feis.link fee
// to the platform, as in a normal checkout (backend card 1bda37b5, PLAN.md).
// Built on the organizer panel's .oir design layer, like Default syllabi.
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import HeaderOrg from '../Headers/HeaderOrg';
import { Msr, useMaterialSymbols } from '../OireachtasPanel/symbols';
import { cancelRequest, listRequests, resendReceipt, resendRequest } from './api';
import NewRequestDialog from './NewRequestDialog';
import RequestDrawer from './RequestDrawer';
import useDialogFocus from './useDialogFocus';
import { EVENT_TYPE_LABELS, STATUS_FILTERS, day, errText, money, statusPill } from './paymentRequestModel';
import '../OireachtasPanel/oireachtas.css';
import '../OireachtasPanel/organizerOireachtas.css';
import '../DefaultSyllabi/defaultSyllabi.css';
import './paymentRequests.css';

const PAGE_SIZE = 25;

// The menu floats above the page (position: fixed at the button) so the
// table's rounded, clipped box can't cut it off, and opens upward when there
// is no room below. Scrolling or resizing closes it.
function RowMenu({ row, onCopy, onResend, onCancel, onResendReceipt }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const ref = useRef(null);
  const btnRef = useRef(null);
  const popRef = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    // Escape is handled on the wrapper (its keydown doesn't reach document).
    const close = (e) => {
      if (!ref.current?.contains(e.target)) setOpen(false);
    };
    const dismiss = () => setOpen(false);
    document.addEventListener('mousedown', close);
    window.addEventListener('resize', dismiss);
    window.addEventListener('scroll', dismiss, true);
    return () => {
      document.removeEventListener('mousedown', close);
      window.removeEventListener('resize', dismiss);
      window.removeEventListener('scroll', dismiss, true);
    };
  }, [open]);
  useLayoutEffect(() => {
    if (!open || !btnRef.current) {
      setPos(null);
      return;
    }
    const b = btnRef.current.getBoundingClientRect();
    const h = popRef.current ? popRef.current.offsetHeight : 0;
    const gap = 4;
    const roomBelow = window.innerHeight - b.bottom - gap;
    const top = roomBelow >= h + 8 ? b.bottom + gap : Math.max(8, b.top - gap - h);
    setPos({ top, right: Math.max(8, window.innerWidth - b.right) });
  }, [open]);
  const pick = (fn) => (e) => {
    e.stopPropagation();
    setOpen(false);
    fn(row);
  };
  const items = [];
  if (row.status === 'open') {
    items.push({ key: 'copy', icon: 'link', t: 'Copy pay link', fn: onCopy });
    items.push({ key: 'resend', icon: 'forward_to_inbox', t: 'Resend email', d: row.lastSentAt ? `Last sent ${day(row.lastSentAt)}` : '', fn: onResend });
    items.push({ key: 'cancel', icon: 'block', t: 'Cancel request', d: 'The link stops working. Not possible while a payment is going through.', fn: onCancel });
  }
  if (row.attention === 'receipt_not_sent') items.push({ key: 'receipt', icon: 'receipt_long', t: 'Resend receipt', fn: onResendReceipt });
  if (!items.length) return null;
  return (
    <div
      className="kebab"
      ref={ref}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => {
        e.stopPropagation();
        if (e.key === 'Escape' && open) {
          e.preventDefault();
          setOpen(false);
          btnRef.current?.focus();
        }
      }}
    >
      <button
        ref={btnRef}
        type="button"
        className={`kebab-btn${open ? ' open' : ''}`}
        aria-label={`Actions for ${row.payer?.name || 'request'}`}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <Msr>more_vert</Msr>
      </button>
      {open && (
        <div
          ref={popRef}
          className="kebab-pop pr-pop"
          role="menu"
          style={pos ? { top: pos.top, right: pos.right } : { visibility: 'hidden' }}
        >
          {items.map((it) => (
            <button type="button" role="menuitem" key={it.key} className="kebab-item pr-menuitem" onClick={pick(it.fn)}>
              <span className="ki-t"><Msr>{it.icon}</Msr>{it.t}</span>
              {it.d && <span className="ki-d">{it.d}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function CancelDialog({ target, onKeep, onConfirm }) {
  const ref = useRef(null);
  useDialogFocus(ref, onKeep, target.busy);
  return (
    <div className="modal-scrim" role="presentation">
      <div className="omodal" role="alertdialog" aria-modal="true" aria-labelledby="pr-cancel-title" ref={ref} tabIndex={-1}>
        <div className="modal-ic"><Msr>block</Msr></div>
        <div id="pr-cancel-title" className="pr-dlg-title" role="heading" aria-level="2">Cancel this request?</div>
        <p>
          {money(target.totalCents, target.currency)} from {target.payer?.name}. The pay link stops working. You can send
          a new request afterwards.
        </p>
        {target.error && <div className="op-error" role="alert">{target.error}</div>}
        <div className="modal-act">
          <button type="button" className="mbtn ghost" onClick={onKeep} disabled={target.busy} data-autofocus>Keep it</button>
          <button type="button" className="mbtn danger" onClick={onConfirm} disabled={target.busy}>
            {target.busy ? 'Cancelling…' : 'Cancel request'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function PaymentRequests() {
  useMaterialSymbols();
  const navigate = useNavigate();
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ requests: [], total: 0, counts: {}, stats: null });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // { text, warn, where: 'page' | 'drawer' } - feedback shows where the action
  // was started, so a drawer action isn't announced behind the inert page.
  const [notice, setNotice] = useState(null);
  // Bumped to reload with the CURRENT criteria (never a captured load closure).
  const [reloadToken, setReloadToken] = useState(0);
  const [creating, setCreating] = useState(false);
  const [drawer, setDrawer] = useState(null); // { id, version }
  const [cancelTarget, setCancelTarget] = useState(null);

  // debounce the search box
  useEffect(() => {
    const next = q.trim();
    if (next === query) return undefined;
    const t = setTimeout(() => {
      setQuery(next);
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [q, query]);

  const seq = useRef(0);
  const load = useCallback(async () => {
    const mine = ++seq.current;
    setLoading(true);
    setError('');
    try {
      const res = await listRequests({ status, q: query, page, limit: PAGE_SIZE });
      if (mine !== seq.current) return;
      // The filtered total shrank below this page (e.g. the only request on the
      // last page was cancelled): go to the new last page, which reloads.
      const lastPage = Math.max(1, Math.ceil((res.total || 0) / PAGE_SIZE));
      if (page > lastPage) {
        setPage(lastPage);
        return;
      }
      setData({ requests: res.requests || [], total: res.total || 0, counts: res.counts || {}, stats: res.stats || null });
    } catch (e) {
      if (mine !== seq.current) return;
      setError(errText(e));
    } finally {
      if (mine === seq.current) setLoading(false);
    }
  }, [status, query, page]);

  // Every list fetch runs from here, so a refresh after an action always uses
  // the criteria on screen when it fires; the seq guard drops any response
  // that a newer fetch has overtaken (review R2-F1).
  useEffect(() => {
    load();
  }, [load, reloadToken]);

  useEffect(() => {
    if (!notice) return undefined;
    const t = setTimeout(() => setNotice(null), 5000);
    return () => clearTimeout(t);
  }, [notice]);

  const reload = () => setReloadToken((t) => t + 1);
  const refresh = () => {
    reload();
    setDrawer((d) => d && { ...d, version: d.version + 1 });
  };

  const copyLink = async (row, where = 'page') => {
    try {
      await navigator.clipboard.writeText(row.payUrl);
      setNotice({ text: 'Pay link copied.', where });
    } catch (e) {
      setNotice({ text: `Copy this link: ${row.payUrl}`, warn: true, where });
    }
  };
  const resend = async (row, where = 'page') => {
    try {
      const res = await resendRequest(row.id);
      setNotice(
        res.emailed === false
          ? { text: res.message || 'The email did not send.', warn: true, where }
          : { text: `Emailed ${row.payer?.email} again.`, where }
      );
      refresh();
    } catch (e) {
      setNotice({ text: errText(e), warn: true, where });
    }
  };
  const receipt = async (row, where = 'page') => {
    try {
      const res = await resendReceipt(row.id);
      setNotice(
        res.emailed === false
          ? { text: res.message || 'The receipt did not send.', warn: true, where }
          : { text: `Receipt emailed to ${row.payer?.email}.`, where }
      );
      refresh();
    } catch (e) {
      setNotice({ text: errText(e), warn: true, where });
    }
  };
  const askCancel = (row) => {
    setDrawer(null);
    setCancelTarget(row);
  };
  const confirmCancel = async () => {
    const row = cancelTarget;
    setCancelTarget({ ...row, busy: true, error: '' });
    try {
      await cancelRequest(row.id);
      setCancelTarget(null);
      setNotice({ text: `Cancelled the request to ${row.payer?.name}.`, where: 'page' });
      reload();
    } catch (e) {
      setCancelTarget({ ...row, busy: false, error: errText(e) });
    }
  };

  const created = (res) => {
    setCreating(false);
    const email = res?.request?.payer?.email;
    setNotice(
      res?.emailed === false
        ? { text: `Request created, but the email did not send: ${res.message || 'try Resend'}.`, warn: true, where: 'page' }
        : { text: `Request sent to ${email || 'the payer'}.`, where: 'page' }
    );
    setStatus('');
    setPage(1);
    reload();
  };

  const stats = data.stats;
  const cur = stats?.currency || 'cad';
  const pages = Math.max(1, Math.ceil(data.total / PAGE_SIZE));
  const menuProps = { onCopy: copyLink, onResend: resend, onCancel: askCancel, onResendReceipt: receipt };
  const drawerProps = {
    onCopy: (r) => copyLink(r, 'drawer'),
    onResend: (r) => resend(r, 'drawer'),
    onCancel: askCancel,
    onResendReceipt: (r) => receipt(r, 'drawer'),
  };
  const pageNotice = notice && notice.where !== 'drawer' ? notice : null;
  const drawerNotice = drawer && notice && notice.where === 'drawer' ? notice : null;

  return (
    <>
      <HeaderOrg />
      <div className="oir op-page pr-page">
        <div className="wrap">
          <button type="button" className="op-back" onClick={() => navigate('/')}>
            <Msr>arrow_back</Msr>Dashboard
          </button>
          <div className="ph">
            <div>
              <div className="pr-h1" role="heading" aria-level="1">Payment requests</div>
              <p className="pr-sub">
                Charge someone an amount after checkout has closed. They get an email with a link, log in and pay by card.
                The organizer’s share goes to the event’s Stripe account and the platform fee comes to us — the same split as
                a normal checkout.
              </p>
            </div>
          </div>

          {stats && (
            <div className="op-stats">
              <div className="op-stat">
                <div className="op-stat-v">{stats.openCount}</div>
                <div className="op-stat-l">Open</div>
                <div className="op-stat-s">{money(stats.openCents, cur)} waiting</div>
              </div>
              <div className="op-stat">
                <div className="op-stat-v">{money(stats.paidThisMonthCents, cur)}</div>
                <div className="op-stat-l">Paid this month</div>
                <div className="op-stat-s">{stats.paidThisMonthCount} {stats.paidThisMonthCount === 1 ? 'request' : 'requests'}</div>
              </div>
              <div className="op-stat">
                <div className="op-stat-v">{money(stats.feesThisMonthCents, cur)}</div>
                <div className="op-stat-l">Platform fees this month</div>
                <div className="op-stat-s">organizers received {money(stats.organizerThisMonthCents, cur)}</div>
              </div>
              <div className="op-stat">
                <div className="op-stat-v">{stats.attentionCount}</div>
                <div className="op-stat-l">Needs attention</div>
                <div className="op-stat-s">receipt not sent, duplicate or mismatched payment</div>
              </div>
            </div>
          )}

          <div className="ds-regions" role="tablist" aria-label="Status">
            {STATUS_FILTERS.map((f) => (
              <button
                key={f.key || 'all'}
                type="button"
                role="tab"
                aria-selected={status === f.key}
                className={`op-chip${f.warn ? ' warn' : ''}${status === f.key ? ' on' : ''}`}
                onClick={() => {
                  setStatus(f.key);
                  setPage(1);
                }}
              >
                {f.label}
                <span className="ds-count">{data.counts?.[f.count] ?? 0}</span>
              </button>
            ))}
          </div>

          <div className="toolbar ds-toolbar">
            <div className="op-search">
              <Msr>search</Msr>
              <input
                type="search"
                placeholder="Search payer, event or note"
                aria-label="Search payment requests"
                value={q}
                onChange={(e) => setQ(e.target.value)}
              />
            </div>
            <button type="button" className="ab-next ds-add" onClick={() => setCreating(true)}>
              <Msr>add</Msr>New request
            </button>
          </div>

          {pageNotice && (
            <div className={`ds-notice${pageNotice.warn ? ' pr-warn' : ''}`} role={pageNotice.warn ? 'alert' : 'status'}>
              <Msr>{pageNotice.warn ? 'error' : 'check_circle'}</Msr>
              {pageNotice.text}
            </div>
          )}

          {error ? (
            <div className="op-error">
              {error}{' '}
              <button type="button" className="op-btn sm" onClick={reload}>Try again</button>
            </div>
          ) : loading && !data.requests.length ? (
            <div className="op-note">Loading…</div>
          ) : data.requests.length === 0 ? (
            <div className="card op-panel op-empty">
              {query || status ? 'No requests match.' : 'No payment requests yet. Use “New request” to charge someone after checkout has closed.'}
            </div>
          ) : (
            <div className="card">
              <div className="op-table" role="table" aria-label="Payment requests">
                <div className="op-tr op-th pr-tr" role="row">
                  <div role="columnheader">Sent</div>
                  <div role="columnheader">Payer</div>
                  <div role="columnheader">Event</div>
                  <div role="columnheader">For</div>
                  <div role="columnheader" className="r">Total</div>
                  <div role="columnheader">Status</div>
                  <div role="columnheader" aria-label="Actions" />
                </div>
                {data.requests.map((r) => {
                  const pill = statusPill(r);
                  return (
                    <div
                      key={r.id}
                      className="op-tr pr-tr op-click"
                      role="row"
                      tabIndex={0}
                      onClick={() => setDrawer({ id: r.id, version: 0 })}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setDrawer({ id: r.id, version: 0 });
                        }
                      }}
                    >
                      <div role="cell" className="pr-when" data-label="Sent">{day(r.createdAt)}</div>
                      <div role="cell" className="pr-who" data-label="Payer">
                        <b>{r.payer?.name}</b>
                        <span>{r.payer?.email}</span>
                      </div>
                      <div role="cell" className="pr-who" data-label="Event">
                        <b>{r.event?.name}</b>
                        <span>{EVENT_TYPE_LABELS[r.event?.type] || r.event?.type} · {r.organizer?.name}</span>
                      </div>
                      <div role="cell" className="pr-for" data-label="For">{r.note}</div>
                      <div role="cell" className="pr-amt" data-label="Total">{money(r.totalCents, r.currency)}</div>
                      <div role="cell" data-label="Status"><span className={`op-pill ${pill.cls}`}>{pill.label}</span></div>
                      <div role="cell" className="pr-kebab"><RowMenu row={r} {...menuProps} /></div>
                    </div>
                  );
                })}
              </div>
              {pages > 1 && (
                <div className="pr-pager">
                  <button type="button" className="op-btn sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>
                    <Msr>chevron_left</Msr>Previous
                  </button>
                  <span className="op-muted op-small">Page {page} of {pages} · {data.total} requests</span>
                  <button type="button" className="op-btn sm" disabled={page >= pages} onClick={() => setPage(page + 1)}>
                    Next<Msr>chevron_right</Msr>
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {creating && <NewRequestDialog onClose={() => setCreating(false)} onCreated={created} />}
        {drawer && (
          <RequestDrawer
            id={drawer.id}
            version={drawer.version}
            onClose={() => {
              setDrawer(null);
              setNotice((n) => (n && n.where === 'drawer' ? null : n));
            }}
            notice={drawerNotice}
            {...drawerProps}
          />
        )}
        {cancelTarget && <CancelDialog target={cancelTarget} onKeep={() => setCancelTarget(null)} onConfirm={confirmCancel} />}
      </div>
    </>
  );
}
